import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ArrowLeft, ArrowRight, ClipboardList } from 'lucide-react';
import { useSolicitation } from '../context/SolicitationContext';
import { getCategory, formatMeters } from '../data/catalog';

export default function RequestPage() {
  const { items, updateMeters, removeItem } = useSolicitation();
  const navigate = useNavigate();

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 md:py-12" data-testid="request-page">
      <h1 className="font-serif text-3xl tracking-tight text-navy md:text-4xl" data-testid="request-title">
        Minha Solicitação
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {items.length} {items.length === 1 ? 'tecido selecionado' : 'tecidos selecionados'} · Nenhum
        pagamento é feito nesta etapa.
      </p>

      {items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="request-empty-state">
          <ClipboardList className="mx-auto h-10 w-10 text-navy/40" strokeWidth={1.5} />
          <p className="mt-4 font-serif text-2xl text-navy">Sua solicitação está vazia</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Explore nossos tecidos e adicione os que você deseja com a metragem ideal.
          </p>
          <Link
            to="/categoria/todos"
            className="mt-6 inline-block rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover"
            data-testid="request-empty-browse-button"
          >
            EXPLORAR TECIDOS
          </Link>
        </div>
      ) : (
        <>
          <ul className="mt-8 divide-y divide-navy/10 rounded-2xl border border-navy/10 bg-white" data-testid="request-items-list">
            {items.map((item) => (
              <li key={item.key} className="flex gap-4 p-4 md:p-5" data-testid={`request-item-${item.key.replace('::', '-')}`}>
                <Link to={`/produto/${item.productSlug}`} className="shrink-0">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.variantName ? `${item.name} - ${item.variantName}` : item.name}
                      className="h-20 w-20 rounded-lg object-cover md:h-24 md:w-24"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-navy-light text-navy/40 md:h-24 md:w-24">
                      <ClipboardList className="h-6 w-6" />
                    </div>
                  )}
                </Link>
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link to={`/produto/${item.productSlug}`} className="font-medium leading-snug hover:text-navy">
                        {item.name}
                      </Link>
                      <p className="text-sm text-muted-foreground" data-testid={`request-item-variant-${item.key.replace('::', '-')}`}>
                        {[item.customRequest ? `Cliente procura: ${item.variantName}` : item.variantName, getCategory(item.categorySlug)?.name].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.key)}
                      aria-label={`Remover ${item.name}${item.variantName ? ` ${item.variantName}` : ''}`}
                      data-testid={`request-item-remove-${item.key.replace('::', '-')}`}
                      className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center gap-2 pt-3">
                    <label htmlFor={`meters-${item.key}`} className="text-xs text-muted-foreground">
                      Metragem:
                    </label>
                    <input
                      id={`meters-${item.key}`}
                      data-testid={`request-item-meters-${item.key.replace('::', '-')}`}
                      inputMode="decimal"
                      value={item.meters == null ? '' : String(item.meters).replace('.', ',')}
                      placeholder="a confirmar"
                      onChange={(e) => {
                        const v = parseFloat(e.target.value.replace(',', '.'));
                        if (!isNaN(v) && v >= 0.01) updateMeters(item.key, Math.min(Math.round(v * 100) / 100, 999.99));
                      }}
                      className="h-10 w-20 rounded-lg border border-input bg-white text-center font-semibold outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                    />
                    <span className="text-xs text-muted-foreground">{item.meters == null ? 'metros — a confirmar' : 'metros'}</span>
                    <div className="ml-2 hidden gap-1 sm:flex">
                      {[1, 2, 3].map((m) => (
                        <button
                          key={m}
                          onClick={() => updateMeters(item.key, m)}
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            item.meters === m ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/60'
                          }`}
                        >
                          {formatMeters(m)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              onClick={() => navigate('/categoria/todos')}
              className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-7 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light"
              data-testid="request-continue-shopping-button"
            >
              <ArrowLeft className="h-4 w-4" /> CONTINUAR ESCOLHENDO
            </button>
            <button
              onClick={() => navigate('/finalizar')}
              className="flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover"
              data-testid="request-checkout-button"
            >
              FINALIZAR SOLICITAÇÃO <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </main>
  );
}
